import type React from "react";
import { useState } from "react";

import { faArrowLeftToLine, faArrowRightToLine } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { CrossIcon } from "assets";
import { useAppConfigContext } from "contexts/AppConfigContext";
import { useProfileContext } from "contexts/ProfileContext";
import { getActionButtonColorStyles } from "shared/atoms/BaseButton/BaseButton";
import { DropInCallPane } from "shared/NavBar/components/DropInCallPane";
import { Logo } from "shared/NavBar/components/Logo";
import { Menu } from "shared/NavBar/components/Menu";
import { MobileTopBar } from "shared/NavBar/components/MobileTopBar";
import { SubMenu } from "shared/NavBar/components/SubMenu";

const NAVBAR_WIDTH = 220;
const NAVBAR_WIDTH_COLLAPSED = 80;
export const NAVBAR_HEIGHT = 56;
const NAVBAR_LOGO_SIZE = 32;

export const NavBar: React.FC<Record<string, never>> = () => {
  const [isMobileNavigationOpen, setIsMobileNavigationOpen] = useState(false);
  const { profile } = useProfileContext();
  const { navBarCollapsed, setNavBarCollapsed } = useAppConfigContext();
  const { t } = useTranslation();
  const showDropInCallPane = profile?.therapist_profile?.feature_flags?.includes("DROP_IN_CALLS_PUSH");

  return (
    <Container $collapsed={navBarCollapsed}>
      <MobileTopBar
        isMobileNavigationOpen={isMobileNavigationOpen}
        setIsMobileNavigationOpen={setIsMobileNavigationOpen}
      />

      <SideBar className={clsx({ collapsed: navBarCollapsed, open: isMobileNavigationOpen })} data-testid="side-bar">
        <CloseButton
          onClick={event => {
            event.stopPropagation();
            setIsMobileNavigationOpen(!isMobileNavigationOpen);
          }}
        >
          <CrossIcon />
        </CloseButton>

        <LogoContainer
          mb={3}
          display="flex"
          height={NAVBAR_LOGO_SIZE}
          justifyContent={navBarCollapsed ? "center" : "space-between"}
          className={navBarCollapsed ? "collapsed" : ""}
        >
          <Box flex={1} overflow="hidden" height={NAVBAR_LOGO_SIZE}>
            {!navBarCollapsed ? <Logo fullLogo /> : <Logo />}
          </Box>
          <CollapseButton
            onClick={() => setNavBarCollapsed(current => !current)}
            title={navBarCollapsed ? t("navbar.expand") : t("navbar.collapse")}
          >
            {navBarCollapsed ? (
              <FontAwesomeIcon icon={faArrowRightToLine} />
            ) : (
              <FontAwesomeIcon icon={faArrowLeftToLine} />
            )}
          </CollapseButton>
        </LogoContainer>

        {profile?.state === "ready" && (
          <MenuWrapper
            onClick={() => {
              setIsMobileNavigationOpen(false);
            }}
          >
            <Menu />
          </MenuWrapper>
        )}

        {showDropInCallPane && <DropInCallPane collapsed={navBarCollapsed} />}

        <SubMenu
          isMobileNavigationOpen={isMobileNavigationOpen}
          setIsMobileNavigationOpen={setIsMobileNavigationOpen}
        />
      </SideBar>
    </Container>
  );
};

const Container = styled.div<{ $collapsed: boolean }>`
  ${props => props.theme.aboveBreakpoint} {
    transition: 0.2s all ease-out;
    width: ${props => (props.$collapsed ? NAVBAR_WIDTH_COLLAPSED : NAVBAR_WIDTH)}px;
    min-width: ${props => (props.$collapsed ? NAVBAR_WIDTH_COLLAPSED : NAVBAR_WIDTH)}px;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 20px;
  right: 16px;
  z-index: 1;
  ${props => props.theme.aboveBreakpoint} {
    display: none;
  }
`;

const CollapseButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border-radius: 50px;

  ${getActionButtonColorStyles()}

  ${props => props.theme.belowBreakpoint} {
    display: none;
  }
`;

const LogoContainer = styled(Box)`
  position: relative;
  &.collapsed a svg {
    max-height: ${NAVBAR_LOGO_SIZE}px;
    max-width: ${NAVBAR_LOGO_SIZE}px;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    opacity: 1;
    pointer-events: default;
    transition: all 0.2s ease-out;
  }
  &.collapsed ${CollapseButton} {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    pointer-events: none;
    transition: none;
  }
`;

const SideBar = styled.div`
  background-color: ${props => props.theme.colors.white};
  z-index: ${props => props.theme.semantic.zIndex.navbar};
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.redesign.db10};
  flex-shrink: 0;
  box-sizing: border-box;
  transition: 0.2s all ease-out;
  height: 100%;
  position: fixed;
  padding: 16px 12px;

  &.collapsed {
    width: ${NAVBAR_WIDTH_COLLAPSED}px;
  }

  &:not(.collapsed) {
    width: ${NAVBAR_WIDTH}px;
  }

  ${props => props.theme.belowBreakpoint} {
    padding: 12px;
    left: -280px;

    &.open {
      left: 0px;
    }
  }
  ${props => props.theme.aboveBreakpoint} {
    left: 0;
  }

  &:hover {
    & ${LogoContainer}.collapsed ${CollapseButton} {
      opacity: 1;
      pointer-events: auto;
    }

    & ${LogoContainer}.collapsed a svg {
      opacity: 0;
      pointer-events: none;
    }
  }
`;

const MenuWrapper = styled.div`
  height: 100%;
`;
